import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Loader, Button } from 'ncoded-component-library';
import ChooseAFolderInDr from 'router/subrouters/DataRoom/components/ChooseAFolderInDr';
import { DataRoomRFICategory } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import useCallbackAsync from 'hooks/useCallbackAsync';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useRfiCategory from 'hooks/useRfiCategory';
import CheckIcon from 'icons/Check.icon';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import { isUndefined } from 'lodash';

import './OrganizeRequestedFilesSelect.styles.scss';

type OrganizeRequestedFilesSelectProps = {
  className?: string;
  dataRoomRfiId: string;
  dataRoomRFICategories: DataRoomRFICategory[];
  onClose: () => void;
};

const OrganizeRequestedFilesSelect: React.FC<
  OrganizeRequestedFilesSelectProps
> = (props) => {
  const { className, dataRoomRfiId, dataRoomRFICategories, onClose } = props;

  const { t } = useTranslation();

  const [currentlyDisplayedFolder, setCurrentlyDisplayedFolder] = useState<
    string | null
  >(null);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFolders, setSelectedFolders] = useState(
    dataRoomRFICategories.reduce<Record<string, string>>(
      (acc, { _id, dataRoomNodeId }) => ({ ...acc, [_id]: dataRoomNodeId }),
      {},
    ),
  );

  const classes = classNames('organize-requested-files-select', className);

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { updateRfi } = useContext(RfiContext);

  const { getCategoryName } = useRfiCategory();

  const canSubmit = useMemo(
    () => !Object.values(selectedFolders).every(isUndefined),
    [selectedFolders],
  );

  const isAllFilesFolderSame = useMemo(() => {
    const values = Object.values(selectedFolders);
    const firstElement = values[0];

    return (
      !isUndefined(firstElement) &&
      values.every((value) => value === firstElement)
    );
  }, [selectedFolders]);

  const isCurrentFolderSelectedForCategory = useMemo(() => {
    if (!selectedCategory) return isAllFilesFolderSame;

    const value = selectedFolders[selectedCategory];

    return !isUndefined(value) && value === currentlyDisplayedFolder;
  }, [
    selectedCategory,
    selectedFolders,
    currentlyDisplayedFolder,
    isAllFilesFolderSame,
  ]);

  const onChooseFolder = useCallback(
    () =>
      setSelectedFolders((old) =>
        selectedCategory
          ? {
              ...old,
              [selectedCategory]: currentlyDisplayedFolder,
            }
          : Object.keys(old).reduce(
              (acc, key) => ({
                ...acc,
                [key]: currentlyDisplayedFolder,
              }),
              {},
            ),
      ),
    [currentlyDisplayedFolder, selectedCategory],
  );

  const [onSubmit, isLoading] = useCallbackAsync(async () => {
    const { data } = await dataRoomApi.rfi.changeUploadDestinationRfi(
      dataRoomRfiId,
      selectedFolders,
    );

    updateRfi(data);
    onClose();
  });

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      <div>
        <div className="organize-requested-files-select__options">
          <span
            className={classNames({ selected: !selectedCategory })}
            onClick={() => setSelectedCategory('')}
          >
            {t('allFiles')}
            {isAllFilesFolderSame && <CheckIcon />}
          </span>

          <div>
            {dataRoomRFICategories.map(({ _id, name }) => (
              <span
                key={_id}
                className={classNames({ selected: _id === selectedCategory })}
                onClick={() => {
                  setSelectedCategory(_id);
                  setCurrentlyDisplayedFolder(selectedFolders[_id] || null);
                }}
              >
                {getCategoryName(name)}
                {!isUndefined(selectedFolders[_id]) && <CheckIcon />}
              </span>
            ))}
          </div>
        </div>

        <div className="organize-requested-files-select__folders">
          <ChooseAFolderInDr
            hasBackUpLevel={false}
            dataRoomId={dataRoomId}
            currenntlySelectedNodeId={currentlyDisplayedFolder}
            onChange={(folderId) =>
              setCurrentlyDisplayedFolder(folderId || null)
            }
          />

          <Button
            {...(isCurrentFolderSelectedForCategory && {
              icon: CheckcircleIcon,
              variant: 'outline',
            })}
            onClick={onChooseFolder}
          >
            {t(
              isCurrentFolderSelectedForCategory ? 'selected' : 'selectFolder',
            )}
          </Button>
        </div>
      </div>

      <div className="actions">
        <Button variant="link" onClick={onClose}>
          {t('cancel')}
        </Button>

        <Button disabled={isLoading || !canSubmit} onClick={onSubmit}>
          {t('saveDestination')}
        </Button>
      </div>
    </div>
  );
};

export default OrganizeRequestedFilesSelect;
